import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Typography, Slider, Stack, CircularProgress } from '@mui/material';
import {
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff,
  Fullscreen,
  FullscreenExit,
  Settings,
  PictureInPicture,
  CameraAlt
} from '@mui/icons-material';
import ApiService from '../../services/api-service';
import { getUserId } from '../../constants/function';

const VideoPlayerEnhanced = ({ 
  video, 
  studioId, 
  onLoadedMetadata,
  onAspectRatioDetected,
  style = {} 
}) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [videoAspectRatio, setVideoAspectRatio] = useState(null);
  const [isVertical, setIsVertical] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [savingThumbnail, setSavingThumbnail] = useState(false);
  const [thumbnailStatus, setThumbnailStatus] = useState('');

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;

    const updateTime = () => setCurrentTime(videoElement.currentTime);
    const updateDuration = () => setDuration(videoElement.duration);

    videoElement.addEventListener('timeupdate', updateTime);
    videoElement.addEventListener('loadedmetadata', updateDuration);
    videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      videoElement.removeEventListener('timeupdate', updateTime);
      videoElement.removeEventListener('loadedmetadata', updateDuration);
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [video]);

  useEffect(() => {
    // Check ownership when video changes
    if (video) {
      checkOwnership();
    }
  }, [video]);

  const checkOwnership = () => {
    const currentUserId = getUserId();
    
    if (!currentUserId || !video) {
      setIsOwner(false);
      return;
    }

    const uploaderIds = [
      video.uploader?._id,
      video.uploader?.id,
      video.uploader, // Direct string ID
      video.createdBy,
      video.uploadedBy,
      video.uploaderID,
      video.uploaderId,
      video.ownerId,
      video.userId,
      video.studioId,
      studioId // Also check the passed studioId
    ].filter(Boolean);
    
    console.log('VideoPlayerEnhanced Ownership Debug:', {
      currentUserId,
      uploaderIds,
      uploader: video.uploader,
      studioId,
      directMatch: String(currentUserId) === String(video.uploader),
      uploaderType: typeof video.uploader
    });
    
    const isVideoOwner = uploaderIds.some(id => 
      String(currentUserId) === String(id)
    );
    
    // Temporary: Also check if user owns the studio
    const isStudioOwner = String(currentUserId) === String(video.studioId);
    
    console.log('VideoPlayerEnhanced - Is video owner:', isVideoOwner);
    console.log('VideoPlayerEnhanced - Is studio owner:', isStudioOwner);
    console.log('VideoPlayerEnhanced - Final ownership decision:', isVideoOwner || isStudioOwner);
    
    // Check if user owns the studio (since currentUserId doesn't match video.uploader but might own studio)
    const isStudioOwnerByUserId = String(currentUserId) === String(studioId);
    const finalOwnership = isVideoOwner || isStudioOwner || isStudioOwnerByUserId;
    
    console.log('VideoPlayerEnhanced - Final ownership decision:', {
      isVideoOwner,
      isStudioOwner, 
      isStudioOwnerByUserId,
      finalOwnership
    });
    
    setIsOwner(finalOwnership);
  };

  const handleLoadedMetadata = (e) => {
    const video = e.target;
    const aspectRatio = video.videoWidth / video.videoHeight;
    setVideoAspectRatio(aspectRatio);
    setIsVertical(aspectRatio < 1);
    
    if (onLoadedMetadata) {
      onLoadedMetadata(e);
    }
    
    if (onAspectRatioDetected) {
      onAspectRatioDetected(aspectRatio);
    }
  };

  const togglePlay = () => {
    const videoElement = videoRef.current;
    if (isPlaying) {
      videoElement.pause();
    } else {
      videoElement.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (event, newValue) => {
    const videoElement = videoRef.current;
    videoElement.currentTime = newValue;
    setCurrentTime(newValue);
  };

  const handleVolumeChange = (event, newValue) => {
    const videoElement = videoRef.current;
    videoElement.volume = newValue;
    setVolume(newValue);
    setIsMuted(newValue === 0);
  };

  const toggleMute = () => {
    const videoElement = videoRef.current;
    if (isMuted) {
      videoElement.volume = volume || 0.5;
      setIsMuted(false);
    } else {
      videoElement.volume = 0;
      setIsMuted(true);
    }
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    
    if (!isFullscreen) {
      // Try standard fullscreen first, then webkit fallback
      if (container.requestFullscreen) {
        container.requestFullscreen();
      } else if (container.webkitRequestFullscreen) {
        container.webkitRequestFullscreen();
      } else if (container.webkitEnterFullscreen) {
        container.webkitEnterFullscreen();
      }
    } else {
      // Try standard exit first, then webkit fallback
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      } else if (document.webkitCancelFullScreen) {
        document.webkitCancelFullScreen();
      }
    }
    setIsFullscreen(!isFullscreen);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const captureCurrentFrame = async () => {
    if (!videoRef.current || !isOwner) return;
    
    setSavingThumbnail(true);
    setThumbnailStatus('Capturing frame...');
    console.log('Setting status:', 'Capturing frame...');
    
    try {
      const videoElement = videoRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set canvas dimensions to match video
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      
      // Draw current video frame to canvas
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob
      const blob = await new Promise(resolve => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      // Create FormData and send to API
      const formData = new FormData();
      formData.append('thumbnail', blob, 'thumbnail.jpg');
      
      // Include current title to avoid server validation issues
      if (video?.title) {
        formData.append('title', video.title);
      }
      
      // Try to include additional fields that might be required
      if (video?.description) {
        formData.append('description', video.description);
      }
      
      // Extract studioId from video data or use passed studioId
      // Try using current user ID if video doesn't have proper studioId
      const currentUserId = getUserId();
      const videoStudioId = studioId || video?.studioId || currentUserId;
      const videoId = video?._id;
      
      console.log('Studio ID options:', {
        passedStudioId: studioId,
        videoStudioId: video?.studioId,
        currentUserId: currentUserId,
        selectedStudioId: videoStudioId
      });
      
      if (!videoStudioId || !videoId) {
        console.error('Cannot determine studio ID or video ID');
        return;
      }
      
      console.log('Updating thumbnail for video:', videoId, 'with studio:', videoStudioId);
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(key, ':', value);
      }
      
      setThumbnailStatus('Uploading thumbnail...');
      console.log('Setting status:', 'Uploading thumbnail...');
      
      // Update video thumbnail using existing API
      const response = await ApiService.UPDATEVIDEO({
        studioId: videoStudioId,
        videoId: videoId,
        formData: formData
      });
      
      if (response.status === 'success' || response.success || response.ok) {
        console.log('Thumbnail updated successfully');
        setThumbnailStatus('Thumbnail saved! Fetching details for video...');
        console.log('Setting status:', 'Thumbnail saved! Fetching details for video...');
        
        // Show success alert
        alert('Thumbnail updated successfully! The page will now reload to show your new thumbnail.');
        
        // Force full page reload to see the new thumbnail
        setTimeout(() => {
          window.location.reload(true); // true forces a hard reload
        }, 1000);
        
      } else {
        console.error('Failed to update thumbnail:', response);
        setThumbnailStatus('Failed to update thumbnail');
        setTimeout(() => {
          setThumbnailStatus('');
        }, 3000);
      }
      
    } catch (error) {
      console.error('Error capturing frame:', error);
      setThumbnailStatus('Error capturing frame');
      setTimeout(() => {
        setThumbnailStatus('');
        setSavingThumbnail(false);
      }, 3000);
    }
  };

  // Dynamic container styles based on video orientation
  const getContainerStyle = () => {
    if (isVertical) {
      return {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#000',
        ...style
      };
    }
    return {
      width: '100%',
      height: '100%',
      position: 'relative',
      backgroundColor: '#000',
      ...style
    };
  };

  // Dynamic video styles based on orientation
  const getVideoStyle = () => {
    if (isVertical) {
      return {
        height: '100%',
        width: 'auto',
        maxWidth: '100%',
        objectFit: 'contain'
      };
    }
    return {
      width: '100%',
      height: '100%',
      objectFit: 'contain'
    };
  };

  return (
    <Box 
      ref={containerRef}
      sx={getContainerStyle()}
      onMouseEnter={() => setShowControls(true)}
      onMouseLeave={() => setShowControls(false)}
      position="relative"
    >
      <video
        ref={videoRef}
        src={video?.url || video}
        style={getVideoStyle()}
        onClick={togglePlay}
        crossOrigin="anonymous"
        playsInline
        webkit-playsinline="true"
        x-webkit-airplay="allow"
      />

      {/* Video Controls Overlay */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
          p: 2,
          opacity: showControls ? 1 : 0,
          transition: 'opacity 0.3s',
        }}
      >
        {/* Progress Bar */}
        <Slider
          value={currentTime}
          max={duration}
          onChange={handleSeek}
          sx={{
            color: '#fff',
            height: 4,
            '& .MuiSlider-thumb': {
              width: 12,
              height: 12,
            },
          }}
        />

        {/* Control Buttons */}
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Play/Pause */}
            <IconButton onClick={togglePlay} sx={{ color: '#fff' }}>
              {isPlaying ? <Pause /> : <PlayArrow />}
            </IconButton>

            {/* Volume */}
            <IconButton onClick={toggleMute} sx={{ color: '#fff' }}>
              {isMuted ? <VolumeOff /> : <VolumeUp />}
            </IconButton>
            <Slider
              value={isMuted ? 0 : volume}
              max={1}
              step={0.1}
              onChange={handleVolumeChange}
              sx={{
                width: 100,
                color: '#fff',
                '& .MuiSlider-thumb': {
                  width: 12,
                  height: 12,
                },
              }}
            />

            {/* Time */}
            <Typography variant="caption" sx={{ color: '#fff', ml: 2 }}>
              {formatTime(currentTime)} / {formatTime(duration)}
            </Typography>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Capture Thumbnail Button - Only show for owner */}
            {isOwner && (
              <IconButton 
                onClick={captureCurrentFrame} 
                disabled={savingThumbnail}
                sx={{ 
                  color: '#fff',
                  '&:disabled': { color: '#666' }
                }}
                title="Save current frame as thumbnail"
              >
                {savingThumbnail ? (
                  <CircularProgress size={20} sx={{ color: '#fff' }} />
                ) : (
                  <CameraAlt />
                )}
              </IconButton>
            )}

            {/* Settings */}
            <IconButton sx={{ color: '#fff' }}>
              <Settings />
            </IconButton>

            {/* Picture in Picture */}
            <IconButton sx={{ color: '#fff' }}>
              <PictureInPicture />
            </IconButton>

            {/* Fullscreen */}
            <IconButton onClick={toggleFullscreen} sx={{ color: '#fff' }}>
              {isFullscreen ? <FullscreenExit /> : <Fullscreen />}
            </IconButton>
          </Stack>
        </Stack>
      </Box>

      {/* Vertical Video Indicator */}
      {isVertical && (
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: 10,
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: '#fff',
            px: 1.5,
            py: 0.5,
            borderRadius: 1,
            fontSize: '12px',
            opacity: showControls ? 1 : 0,
            transition: 'opacity 0.3s',
          }}
        >
          Vertical Video
        </Box>
      )}
      
      {/* Thumbnail Capture Status */}
      {thumbnailStatus && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0,0,0,0.8)',
            color: '#fff',
            px: 3,
            py: 2,
            borderRadius: 2,
            fontSize: '16px',
            fontWeight: 'bold',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            zIndex: 9999,
          }}
        >
          {thumbnailStatus}
        </Box>
      )}
    </Box>
  );
};

export default VideoPlayerEnhanced;